import { createSignal, createEffect, onCleanup, onMount, untrack, on, For, Show } from "solid-js";
import { sanitizeTooltipHtml } from '@utils/htmlSanitization';
import { Portal } from "solid-js/web";
import * as d3 from "d3";

import Loading from "../../utilities/Loading";

import { persistantStore } from "../../../store/persistantStore";

import {
  selectedGroupKeys,
  setSelectedGroupKeys,
  selectedEvents,
  triggerUpdate,
  setTriggerUpdate,
  triggerSelection,
  setTriggerSelection,
  isEventHidden,
  setHasSelection,
  setSelection,
  selectedRange,
  highlightedEventIds,
} from "../../../store/selectionStore";
import { tooltip, setTooltip, eventType, filtered, color, phase, tabledata, groupDisplayMode } from "../../../store/globalStore";
import { MANEUVER_EVENT_TYPES } from "../../../utils/maneuverEventType";
import { selectedGradesManeuvers, setSelectedGradesManeuvers } from "../../../store/filterStore";

import { myTickFormat, putData, round } from "../../../utils/global";
import { error as logError, debug } from "../../../utils/console";
import { buildGroupedIndividualPaths, computeGroupedIndividualPathsOffThread, type GroupedIndividualPath } from "../../../utils/maneuverPathsBuild";
import { fetchTimeSeriesData } from "../../../services/maneuversDataService";
import {
  buildTimeseriesDescription,
  fetchManeuverTimeseriesOptions,
  isManeuverTimeseriesChartChannel,
  parseTimeSeriesResponse,
} from "../../../utils/maneuverTimeseriesGroups";
import {
  buildColorGrouping,
  getGroupKeyFromItem,
  getTimeSeriesGroupKeyFromPoint,
  groupKeyEquals,
  maneuverGroupByField,
} from "../../../utils/colorGrouping";
import { isDark } from "../../../store/themeStore";
import { apiEndpoints } from "../../../config/env";
import { user } from "../../../store/userStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../../utils/performanceLocalSelection";
import { maneuverColorLegendRenderedPx } from "../../../utils/maneuverLegendHeight";
import { maneuverBaselineStore } from "../../../store/maneuverBaselineStore";
import { baselineTimeseriesChannelExtent, baselineTimeseriesForChannel, readBaselineTimeseriesValue } from "../../../utils/maneuverBaselineTracks";

function escapeSelectorId(id: string): string {
  if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
    return CSS.escape(id);
  }
  return id.replace(/([!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~])/g, "\\$1");
}

interface TimeSeriesProps {
  context?: 'dataset' | 'fleet' | 'historical';
  /** When omitted, component runs multi-group lazy sections (BASICS + options from API). */
  description?: string;
  /** Force multi-group mode even if description is passed (optional). */
  multiGroup?: boolean;
  onDataUpdate?: () => void;
  instanceId?: string;
  panelActive?: () => boolean;
  onLoadStateChange?: (loading: boolean) => void;
}

interface TimeSeriesDataPoint {
  event_id: number;
  tws_bin: number;
  tack: string;
  vmg_perc_avg: number;
  race: number | string;
  source_name: string;
  State?: string;
  Config?: string;
  time: number;
  [key: string]: any; // For dynamic channels
}

export default function TimeSeries(props: TimeSeriesProps) {
  // Get context from props, default to 'dataset' for backward compatibility
  const context = props?.context || 'dataset';
  const [tsdata, setTsData] = createSignal<TimeSeriesDataPoint[][]>([]);
  const [mode_x] = createSignal(true);
  const [loading, setLoading] = createSignal(true);
  const [firstload, setFirstLoad] = createSignal(true);
  const [channelOrder, setChannelOrder] = createSignal<string[]>([]);
  const [chartsFromData, setChartsFromData] = createSignal<string[]>([]);

  const isPanelActive = (): boolean => {
    const fn = props.panelActive;
    return fn ? fn() : true;
  };

  createEffect(() => {
    if (!isPanelActive()) return;
    props.onLoadStateChange?.(loading());
  });

  /** Multi-group lazy sections: no description prop (or explicit multiGroup). */
  const isMultiGroupMode = () => props.multiGroup === true || props.description === undefined;

  const [groupOptionKeys, setGroupOptionKeys] = createSignal<string[]>(["BASICS"]);
  type GroupSeriesStatus = "idle" | "loading" | "loaded" | "error";
  type GroupSeriesEntry = {
    status: GroupSeriesStatus;
    data?: TimeSeriesDataPoint[][];
    charts?: string[];
    channelOrder?: string[];
  };
  const [groupSeriesState, setGroupSeriesState] = createSignal<Record<string, GroupSeriesEntry>>({});
  let multiFetchAbort: AbortController | null = null;
  let multiSessionCounter = 0;
  const groupSectionObservers = new Map<string, IntersectionObserver>();
  const groupPlotMounts = new Map<string, HTMLDivElement | null>();
  let multiPlotsRootEl: HTMLDivElement | null = null;

  // Viewport tracking for performance optimization
  const [chartVisibility, setChartVisibility] = createSignal<Map<string, boolean>>(new Map());
  const getViewportBuffer = () => {
    const chartHeightWithGap = chartHeight + 8;
    return chartHeightWithGap * 3;
  };
  let intersectionObservers = new Map<string, IntersectionObserver>();
  let updateTimeSeriesDebounce: ReturnType<typeof setTimeout> | null = null;
  let lastExtractedChartsLogKey = '';
  let storedMouseHandlers = new Map<string, {
    mouseover: ((event: MouseEvent, d: TimeSeriesDataPoint[]) => void) | null;
    mouseout: (() => void) | null;
    mousemove: ((event: MouseEvent, d: TimeSeriesDataPoint[]) => void) | null;
    click: ((event: MouseEvent, d: any) => void) | null;
  }>();

  let timeArray: any[][] = []
  let valArray: any[][] = []
  let eidArray: number[] = []

  let chartHeight = 200
  let chartWidth = 1280
  let containerRef: HTMLDivElement | null = null
  let plotsEl: HTMLDivElement | null = null

  const domIdPrefix = () => (props.instanceId ? `${props.instanceId}-` : "");

  let lastResizeObservedWidth = 0;
  let lastAppliedLayoutHeightPx = 0;
  let resizeDebounceTimer: ReturnType<typeof setTimeout> | null = null;
  // One-shot guard: set true right before updateContainerHeight writes the container/area heights, so the
  // ResizeObserver cycle that our own write triggers is skipped (only when width is unchanged). Without
  // this, measuring the now-taller plotsRoot feeds back into updateContainerHeight and grows it endlessly.
  let suppressSelfResizeOnce = false;

  const getElementLayoutHeight = (element: HTMLElement | null, scaleFactor: number): number => {
    if (!element) return 0;
    const scale = Number.isFinite(scaleFactor) && scaleFactor > 0 ? scaleFactor : 1;
    const elementRect = element.getBoundingClientRect();
    const rectHeight = elementRect.height / scale;
    let childBottom = 0;
    Array.from(element.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      const childRect = child.getBoundingClientRect();
      childBottom = Math.max(childBottom, (childRect.bottom - elementRect.top) / scale);
    });
    return Math.max(element.scrollHeight, element.offsetHeight, rectHeight, childBottom);
  };

  const getRenderedChartStackHeight = (scaleFactor: number): number =>
    getElementLayoutHeight(multiPlotsRootEl ?? plotsEl, scaleFactor);

  // Using global color scale for consistency
  let myColorScale: d3.ScaleLinear<number, string> | d3.ScaleThreshold<number, string> | d3.ScaleOrdinal<string | number, string> = d3.scaleLinear<number, string>()
  let getItemColor: ((d: any) => string) | null = null; // Store getItemColor function for SOURCE coloring
  let minVal = 999999
  let maxVal = -999999

  const buildDescription = (): string =>
    buildTimeseriesDescription(phase(), String(props?.description || "BASICS").toUpperCase());


  const updateChartWidth = (): void => {
    if (containerRef) {
      const containerWidth = containerRef.offsetWidth || 1280;
      chartWidth = Math.max(containerWidth - 20, 400); // Use container width with 20px margin, min 400px
    } else {
      chartWidth = 1280; // Fallback width
    }
  }

  const updateChartHeight = (): void => {
    chartHeight = 200; // Fixed 200px height
  }

  // Async function to fetch data
  const fetchData = async (): Promise<TimeSeriesDataPoint[][]> => {
    try {
      // Get the current filtered event list - time series endpoint requires non-empty event_list
      const currentFiltered = filtered();
      // Extract event IDs from filtered data (handles both number[] and ManeuverData[])
      let eventIds: number[] = [];
      if (Array.isArray(currentFiltered) && currentFiltered.length > 0) {
        if (typeof currentFiltered[0] === 'number') {
          eventIds = currentFiltered as unknown as number[];
        } else {
          eventIds = (currentFiltered as any[]).map((item: any) => item.event_id).filter((id: any): id is number => typeof id === 'number');
        }
      }
      
      const hasEvents = eventIds.length > 0;
      
      // Time series endpoint requires event_list to be non-empty, so we must have events
      if (!hasEvents) {
        // No events yet (e.g. table still loading or no events selected) – skip fetch for now
        debug('TimeSeries: Skipping fetch - no events in filtered list');
        return [];
      }
      
      // Build description from phase and selected description
      const descriptionStr = buildDescription();

      if (eventIds.length === 0) {
        debug('TimeSeries: No valid event IDs found in filtered list');
        return [];
      }

      const fetchParams: {
        eventType: string;
        description: string;
        eventList: number[];
      } = {
        eventType: eventType(),
        description: descriptionStr,
        eventList: eventIds,
      };

      debug("TimeSeries (grouped): fetchParams before calling fetchTimeSeriesData:", {
        hasEventList: !!fetchParams.eventList,
        eventListLength: fetchParams.eventList?.length || 0,
      });

      const timeseriesResponse = await fetchTimeSeriesData(context, fetchParams);

      const parsed = parseTimeSeriesResponse({
        timeseriesResponse,
        eventIds,
        isEventHidden,
        tableRows: tabledata() as Array<Record<string, unknown>>,
        phaseVal: phase(),
      });
      const dataArray = parsed.dataArray as TimeSeriesDataPoint[][];
      if (parsed.charts.length > 0) {
        setChartsFromData(parsed.charts);
        const digest = parsed.charts.join("\0");
        if (digest !== lastExtractedChartsLogKey) {
          lastExtractedChartsLogKey = digest;
          debug("TimeSeries (grouped): charts from response:", parsed.charts);
        }
      }
      if (parsed.channelOrder.length > 0) {
        setChannelOrder(parsed.channelOrder);
      }

      if (!dataArray || dataArray.length === 0) {
        return [];
      }

      setTsData(dataArray);
      updateMinMaxRanges();
      return dataArray;
    } catch (error: any) {
      logError("Error fetching data:", error);
      return [];
    }
  };

  function updateMinMaxRanges() {
    let channel = 'tws_bin'
    if (color() == 'VMG') {
      channel = 'vmg_perc_avg'
    }

    minVal = 9999999
    maxVal = -9999999
    tsdata().forEach(function(item) {
      try {
        let d = item[0]
        let val = parseFloat(d[channel])
        if (val > maxVal) {maxVal = val}
        if (val < minVal) {minVal = val}
      } catch {
        /* ignore non-numeric values */
      }
    })

    InitScales()  
  }

  function InitScales(): void {
    const currentColor = String(color() || '').toUpperCase();
    
    if (currentColor === 'TWS') {
      // Fixed TWS bins: 10, 15, 20, 25, 30, 35, 40, 45, 50
      // Use scaleThreshold with 8 thresholds to create 9 color ranges (one for each bin)
      const twsScale = d3.scaleThreshold<number, string>()
      twsScale.domain([12.5, 17.5, 22.5, 27.5, 32.5, 37.5, 42.5, 47.5]);
      twsScale.range(["blue","lightblue","cyan","lightgreen","yellow","orange","red","darkred","purple"] as any)
      myColorScale = twsScale as any;
    }
    else if (currentColor === 'VMG') {
      const vmgScale = d3.scaleLinear<number, string>()
      vmgScale.domain([minVal, (minVal + maxVal) / 2, maxVal])
      vmgScale.range(["blue","lightgrey","red"] as any)
      myColorScale = vmgScale as any;
    }
    else if (currentColor === 'TACK') {
      const tackScale = d3.scaleThreshold<number, string>()
      tackScale.domain([-180,-1,1,180])
      tackScale.range(["red","red","#64ed64","#64ed64"] as any)
      myColorScale = tackScale as any;
    }
    else if (
      currentColor === "RACE" ||
      currentColor === "SOURCE" ||
      currentColor === "STATE" ||
      currentColor === "CONFIG" ||
      currentColor === "CREW" ||
      currentColor === "TESTS" ||
      currentColor === "YEAR" ||
      currentColor === "EVENT"
    ) {
      // Use buildColorGrouping for consistency with ManeuverScatter
      const data: TimeSeriesDataPoint[] = [];
      tsdata().forEach((item) => {
        data.push(item[0]);
      });
      const { scale, getItemColor: getItemColorFn } = buildColorGrouping(data, currentColor);
      myColorScale = scale as any;
      getItemColor = getItemColorFn as ((d: any) => string) | null; // Store the function for use in getOriginalColor
    }
    else {
      const defaultScale = d3.scaleLinear<number, string>()
      //defaultScale.domain([4, 8, 14, 18, 22])
      defaultScale.domain([8, 16, 28, 36, 44]);
      defaultScale.range(["yellow","orange","red"] as any)
      myColorScale = defaultScale as any;
    }
  }

  function getClass(d: TimeSeriesDataPoint): string {
    if (d.tack  == 'PORT') {
        return "dash_line"
    } else if (d.tack == 'STBD') {
      return "solid_line"
    } else if (d.tack == 'P - S') {
        return "solid_line"
    } else {
        return "dash_line"
    }
  }

  // Get the original color based on current color-by setting (without selection-based coloring)
  function getOriginalColor(d: TimeSeriesDataPoint): string {
    if (!d) return 'grey';
    
    const currentColor = String(color() || '').toUpperCase();
    
    if (currentColor === 'TWS') {
      const val = d.tws_bin;
      return String((myColorScale as any)(val) || 'grey');
    }
    else if (currentColor === 'VMG') {
      const val = d.vmg_perc_avg;
      return String((myColorScale as any)(val) || 'grey');
    }
    else if (currentColor === 'TACK') {
      if (d.tack === 'PORT' || d.tack === 'S - P') {
        return '#d62728';
      } else if (d.tack === 'STBD' || d.tack === 'P - S') {
        return '#2ca02c';
      }
    }
    else if (
      currentColor === "RACE" ||
      currentColor === "SOURCE" ||
      currentColor === "STATE" ||
      currentColor === "CONFIG" ||
      currentColor === "CREW" ||
      currentColor === "TESTS" ||
      currentColor === "YEAR" ||
      currentColor === "EVENT"
    ) {
      if (getItemColor) {
        return getItemColor(d) || "grey";
      }
      return "grey";
    }
    return "grey";
  }

  function getColor(d: TimeSeriesDataPoint): string {
    return getOriginalColor(d);
  }
  
  function make_x_gridlines(xScale: d3.ScaleLinear<number, number>) {
    return d3.axisBottom(xScale).ticks(10).tickFormat((d) => myTickFormat(Number(d)));
  }

  function make_y_gridlines(yScale: d3.ScaleLinear<number, number>) {
    return d3.axisLeft(yScale).ticks(5).tickFormat((d) => myTickFormat(Number(d)));
  }

  function tsColorMode(): string {
    return String(color() || "TWS").toUpperCase();
  }

  /** Column name on averaged rows; grouping value comes from getGroupKeyFromItem(..., tsColorMode()). */
  function tsGroupOutputKey(): string {
    return maneuverGroupByField(tsColorMode());
  }

  function groupAndAverageTS(
    collection: TimeSeriesDataPoint[][],
    outputKey: string,
    colorMode: string,
    excludeKeys: string[] = []
  ): TimeSeriesDataPoint[][] {
    /** IQR-based mean: treat 0 as missing when any non-zero present; fallback to simple mean if no inliers. */
    function averageExcludingOutliers(arr: number[]): number {
      if (arr.length === 0) return NaN;
      const nonZero = arr.filter(v => v !== 0 && v === v);
      const toAverage = nonZero.length > 0 ? nonZero : arr;
      if (toAverage.length <= 2) return toAverage.reduce((a, b) => a + b, 0) / toAverage.length;
      const sorted = [...toAverage].sort((a, b) => a - b);
      const q1 = sorted[Math.floor(sorted.length * 0.25)];
      const q3 = sorted[Math.floor(sorted.length * 0.75)];
      const iqr = q3 - q1;
      const lower = q1 - 1.5 * iqr;
      const upper = q3 + 1.5 * iqr;
      const inliers = sorted.filter(v => v >= lower && v <= upper);
      if (inliers.length === 0) return toAverage.reduce((a, b) => a + b, 0) / toAverage.length;
      return inliers.reduce((a, b) => a + b, 0) / inliers.length;
    }

    type Bucket = {
      groupByValue: any;
      time: number;
      valuesByChannel: Record<string, number[]>;
      values: Record<string, any>;
      seriesIndices: Set<number>;
    };
    const grouped: Record<string, Bucket> = {};
    const isNumeric = (v: any): boolean => !isNaN(parseFloat(v)) && isFinite(v);
    const coll = collection || [];
    const modeUpper = String(colorMode || "TWS").toUpperCase();
    // How many series (events) have each groupValue — we only average a timestep when all are present.
    const expectedCountPerGroup = new Map<string | number, number>();
    coll.forEach((points) => {
      if (points[0] == null) return;
      const gval = getTimeSeriesGroupKeyFromPoint(points[0] as Record<string, unknown>, modeUpper);
      expectedCountPerGroup.set(gval, (expectedCountPerGroup.get(gval) ?? 0) + 1);
    });
    // Tag each point with its series index so we can count distinct series per bucket.
    const flat = coll.flatMap((points, seriesIndex) => points.map(p => ({ ...p, __seriesIndex: seriesIndex })));
    flat.forEach(pt => {
      const tNorm = parseFloat(Number(pt.time).toFixed(10));
      const gval = getTimeSeriesGroupKeyFromPoint(pt as Record<string, unknown>, modeUpper);
      const key = `${String(gval)}_${tNorm}`;

      if (!grouped[key]) {
        grouped[key] = { groupByValue: gval, time: tNorm, valuesByChannel: {}, values: {}, seriesIndices: new Set() };
      }
      const bucket = grouped[key];
      bucket.seriesIndices.add((pt as any).__seriesIndex);
      Object.keys(pt).forEach(k => {
        if (k === "time" || k === outputKey || k === "__eventIndex" || k === "__seriesIndex") return;
        if (excludeKeys.includes(k)) {
          bucket.values[k] = (pt as any)[k];
          return;
        }
        const value = (pt as any)[k];
        if (isNumeric(value)) {
          const num = parseFloat(value);
          if (!bucket.valuesByChannel[k]) bucket.valuesByChannel[k] = [];
          bucket.valuesByChannel[k].push(num);
        } else {
          bucket.values[k] = value;
        }
      });
    });
    // Only include a timestep if every series in that group has a point (all points present).
    const expectedFor = (gv: any) => expectedCountPerGroup.get(gv) ?? 0;
    const averaged = Object.values(grouped)
      .filter(g => g.seriesIndices.size === expectedFor(g.groupByValue))
      .map(g => {
        const gv = g.groupByValue;
        const out: Record<string, unknown> = { [outputKey]: gv, time: g.time };
        if (modeUpper === "YEAR") {
          out.year = gv;
          out.Year = gv;
          out.YEAR = gv;
        } else if (modeUpper === "EVENT") {
          out.event = gv;
          out.Event = gv;
          out.EVENT = gv;
        }
        Object.keys(g.valuesByChannel).forEach(k => {
          if (excludeKeys.includes(k)) return;
          const arr = g.valuesByChannel[k];
          out[k] = round(arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : NaN, 2);
        });
        Object.keys(g.values).forEach(k => {
          if (!excludeKeys.includes(k)) out[k] = g.values[k];
        });
        return out as TimeSeriesDataPoint;
      });

    // Group by group value into arrays, sorted by time
    const byGroup = new Map<string | number, TimeSeriesDataPoint[]>();
    averaged.forEach(p => {
      const gv = (p as Record<string, unknown>)[outputKey] as string | number;
      if (!byGroup.has(gv)) byGroup.set(gv, []);
      byGroup.get(gv)!.push(p);
    });
    byGroup.forEach(arr => arr.sort((a,b) => a.time - b.time));
    return Array.from(byGroup.values());
  }

  function buildTimeSeries(
    data: TimeSeriesDataPoint[][],
    scoped?: {
      mountEl: HTMLDivElement;
      groupKey: string;
      chartsOrder: string[];
      channelOrderStored: string[];
    }
  ) {
    const mount = scoped?.mountEl ?? plotsEl;
    if (!mount) {
      debug("TimeSeries (grouped): plots container not ready, skip buildTimeSeries");
      return;
    }
    const idp = domIdPrefix();
    const groupKeySeg = scoped?.groupKey ?? "";
    const idSeg = groupKeySeg ? `${groupKeySeg}-` : "";
    const plotsSelection = d3.select(mount);
    plotsSelection.selectAll("*").remove();

    if (scoped) {
      const prefix = `${idp}${idSeg}`;
      intersectionObservers.forEach((observer, key) => {
        if (key.startsWith(prefix)) {
          observer.disconnect();
          intersectionObservers.delete(key);
        }
      });
      setChartVisibility((prev) => {
        const next = new Map(prev);
        for (const k of next.keys()) {
          if (k.startsWith(prefix)) {
            next.delete(k);
          }
        }
        return next;
      });
    } else {
      intersectionObservers.forEach((observer) => {
        observer.disconnect();
      });
      intersectionObservers.clear();
      setChartVisibility(new Map());
    }

    updateChartWidth();
    updateChartHeight();

    if (data.length > 0 && data[0].length > 0) {
      const firstPoint = data[0][0];

      const excludedFields = new Set([
        "event_id",
        "tack",
        "race",
        "source_name",
        "tws_bin",
        "time",
        "vmg_perc_avg",
        "sink_min",
        "State",
        "state",
        "STATE",
        "Config",
        "config",
        "CONFIG",
        "Crew",
        "crew",
        "CREW",
        "CREW_CODE",
        "Tests",
        "tests",
        "TESTS",
        "TEST",
        "Year",
        "year",
        "YEAR",
        "event",
        "Event",
        "EVENT",
        "event_type",
        "Event_type",
        "EVENT_TYPE",
      ]);

      const chartsOrder = scoped?.chartsOrder ?? chartsFromData();
      const storedOrder = scoped?.channelOrderStored ?? channelOrder();
      const ts_columns: string[] = [];

      const includeChartChannel = (k: string, requirePoint: boolean): boolean =>
        !excludedFields.has(k) &&
        isManeuverTimeseriesChartChannel(k) &&
        (!requirePoint || firstPoint[k] !== undefined);

      if (chartsOrder.length > 0) {
        chartsOrder.forEach((k) => {
          if (includeChartChannel(k, true)) {
            ts_columns.push(k);
          }
        });
      } else if (storedOrder.length > 0) {
        storedOrder.forEach((k) => {
          if (includeChartChannel(k, true)) {
            ts_columns.push(k);
          }
        });
      } else {
        Object.keys(firstPoint).forEach((k) => {
          if (includeChartChannel(k, false)) {
            ts_columns.push(k);
          }
        });
      }

      timeArray = [];
      valArray = [];
      eidArray = [];
      const outputKey = tsGroupOutputKey();
      const colorMode = tsColorMode();
      const mixMode = groupDisplayMode() === "MIX";
      const individualCollections = mixMode ? data : null;
      const groupedCollections = groupAndAverageTS(data, outputKey, colorMode, [
        "event_id",
        "tws_bin",
        "vmg_bin",
        "race",
        "tack",
        "source_name",
        "State",
        "Config",
        "Year",
        "year",
        "YEAR",
        "event",
        "Event",
        "EVENT",
        "event_type",
        "Event_type",
        "EVENT_TYPE",
      ]);

      for (let c = 1; c < ts_columns.length + 1; c++) {
        const channel = ts_columns[c - 1];
        const channelSlug = String(channel).replace(/[^a-zA-Z0-9_-]/g, "_");
        const chartId = `${idp}${idSeg}timeseries-chart-${channelSlug}`;
        const svgPlotId = `${idp}${idSeg}ts${c}`;

        const chartWrapper = plotsSelection
          .append("div")
          .attr("class", "timeseries-chart-wrapper")
          .attr("id", chartId)
          .attr("data-channel", channel)
          .attr("data-chart-search-title", String(channel).toUpperCase())
          .attr("data-chart-search-detail", groupKeySeg)
          .style("width", `${chartWidth}px`)
          .style("flex-shrink", "0");

        chartWrapper
          .append("svg")
          .attr("id", svgPlotId)
          .attr("class", "ts")
          .attr("width", chartWidth)
          .attr("height", chartHeight);

        const chartElement = document.getElementById(chartId);
        if (chartElement) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                const currentVisibility = chartVisibility();
                const wasVisible = currentVisibility.get(chartId) || false;
                const isVisible = entry.isIntersecting;

                const rect = chartElement.getBoundingClientRect();
                const isActuallyVisible =
                  rect && rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;

                if (wasVisible !== isVisible) {
                  const newVisibility = new Map(currentVisibility);
                  newVisibility.set(chartId, isVisible);
                  setChartVisibility(newVisibility);
                }

                if (isVisible || isActuallyVisible) {
                  const bodyId = `${idp}${idSeg}body${c}`;
                  const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
                  const hasBody = !plot.select(`#${escapeSelectorId(bodyId)}`).empty();

                  if (!hasBody) {
                    plot
                      .append("g")
                      .attr("id", bodyId)
                      .attr("transform", "translate(20, 5)");
                    const body = plot.select(`#${escapeSelectorId(bodyId)}`);
                    drawTimeSeriesOffThread(c - 1, body, groupedCollections, channel, true, individualCollections, chartId);
                  }
                } else if (!isVisible && wasVisible && !isActuallyVisible) {
                  const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
                  plot.selectAll("*").remove();
                  storedMouseHandlers.delete(chartId);
                }
              });
            },
            {
              rootMargin: `${getViewportBuffer()}px 0px ${getViewportBuffer()}px 0px`,
              threshold: 0.01,
            }
          );

          observer.observe(chartElement);
          intersectionObservers.set(chartId, observer);

          requestAnimationFrame(() => {
            const rect = chartElement.getBoundingClientRect();
            const isInitiallyVisible =
              rect && rect.top < window.innerHeight && rect.bottom > 0 && rect.width > 0 && rect.height > 0;
            if (isInitiallyVisible) {
              const bodyId = `${idp}${idSeg}body${c}`;
              const plot = d3.select(mount).select(`#${escapeSelectorId(svgPlotId)}`);
              const hasBody = !plot.select(`#${escapeSelectorId(bodyId)}`).empty();
              if (!hasBody) {
                const newVisibility = new Map(chartVisibility());
                newVisibility.set(chartId, true);
                setChartVisibility(newVisibility);
                plot
                  .append("g")
                  .attr("id", bodyId)
                  .attr("transform", "translate(20, 5)");
                const body = plot.select(`#${escapeSelectorId(bodyId)}`);
                drawTimeSeriesOffThread(c - 1, body, groupedCollections, channel, true, individualCollections, chartId);
              }
            }
          });
        }
      }

      const fakeChartWrapper = plotsSelection
        .append("div")
        .attr("class", "timeseries-chart-wrapper timeseries-chart-spacer")
        .style("width", `${chartWidth}px`)
        .style("flex-shrink", "0")
        .style("opacity", "0");

      fakeChartWrapper
        .append("svg")
        .attr("id", `${idp}${idSeg}ts-spacer`)
        .attr("class", "ts")
        .attr("width", chartWidth)
        .attr("height", 24);

      requestAnimationFrame(() => {
        updateContainerHeight();
      });

      setTimeout(() => {
        updateSelection();
      }, 0);
    }
  }

  function getToolTipPosition(event: MouseEvent) {
    try {
      if (!containerRef) return { x: 0, y: 0 };
      
      // Use fixed positioning relative to viewport
      const mouseX = event.clientX;
      const mouseY = event.clientY;
      
      const pad = 12;
      const estWidth = 220;
      const estHeight = 140;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Find the scroll container (could be body, html, or main-content)
      let scrollContainer: HTMLElement | null = null;
      const mainContent = document.getElementById('main-content');
      if (mainContent && mainContent.scrollHeight > mainContent.clientHeight) {
        scrollContainer = mainContent;
      } else if (document.body.scrollHeight > document.body.clientHeight) {
        scrollContainer = document.body;
      } else if (document.documentElement.scrollHeight > document.documentElement.clientHeight) {
        scrollContainer = document.documentElement;
      }
      
      // Get scroll position to understand where we are on the page
      const scrollY = scrollContainer ? scrollContainer.scrollTop : window.scrollY || 0;
      const scrollX = scrollContainer ? scrollContainer.scrollLeft : window.scrollX || 0;
      
      // Calculate tooltip position relative to viewport
      // Position to the right of cursor by default
      let x = mouseX + pad;
      // If tooltip would go off right edge, position to the left of cursor
      if (x + estWidth > viewportWidth) {
        x = Math.max(pad, mouseX - estWidth - pad);
      }
      
      // Position above cursor by default (with small offset)
      let y = mouseY - estHeight - pad;
      
      // If tooltip would go off top of viewport, position below cursor
      if (y < pad) {
        y = mouseY + pad;
        // If still off bottom, position above cursor but within viewport
        if (y + estHeight > viewportHeight) {
          y = Math.max(pad, viewportHeight - estHeight - pad);
        }
      } else if (y + estHeight > viewportHeight) {
        // If tooltip would go off bottom, position above cursor
        y = Math.max(pad, mouseY - estHeight - pad);
      }

      // Translate tooltip 75px to the left of computed position (match standard)
      x -= 75;

      return { x, y };
    } catch (error) {
      logError('Error calculating tooltip position:', error);
      return { x: 0, y: 0 };
    }
  }

  // Compute this chart's MIX individual paths on a @nodets pool, then draw. Group keys (which need the
  // reactive colour mode) are computed on the main thread; falls back to inline draw on rejection or when
  // not in MIX mode. Charts draw once as they scroll into view, so several can crunch concurrently.
  function drawTimeSeriesOffThread(
    index: number,
    chartbody: d3.Selection<d3.BaseType, unknown, null, undefined>,
    collection: TimeSeriesDataPoint[][],
    channel: string,
    forceVisible?: boolean,
    individualCollection: TimeSeriesDataPoint[][] | null = null,
    chartWrapperId?: string
  ) {
    const mixMode = groupDisplayMode() === "MIX";
    if (mixMode && individualCollection) {
      const isVmg = String(channel || "").toUpperCase() === "VMG_PERC";
      const colorName = tsColorMode();
      const groupKeys = individualCollection.map((vals) =>
        String(getGroupKeyFromItem(vals[0] as Record<string, unknown>, colorName))
      );
      computeGroupedIndividualPathsOffThread({ individualCollection: individualCollection as any, channel, isVmgPercChannel: isVmg, groupKeys })
        .then((paths) => drawTimeSeries(index, chartbody, collection, channel, forceVisible, individualCollection, chartWrapperId, paths))
        .catch(() => drawTimeSeries(index, chartbody, collection, channel, forceVisible, individualCollection, chartWrapperId));
    } else {
      drawTimeSeries(index, chartbody, collection, channel, forceVisible, individualCollection, chartWrapperId);
    }
  }

  function drawTimeSeries(
    index: number,
    chartbody: d3.Selection<d3.BaseType, unknown, null, undefined>,
    collection: TimeSeriesDataPoint[][],
    channel: string,
    forceVisible?: boolean,
    individualCollection: TimeSeriesDataPoint[][] | null = null,
    chartWrapperId?: string,
    precomputedIndividualPaths?: GroupedIndividualPath[]
  ) {
    const wrapperId = chartWrapperId ?? `${domIdPrefix()}timeseries-chart-${String(channel).replace(/[^a-zA-Z0-9_-]/g, "_")}`;
    const currentVisibility = chartVisibility();
    const isChartVisible = Boolean(forceVisible) || currentVisibility.get(wrapperId) || false;
    if (!isChartVisible && chartbody.selectAll("*").size() > 0) {
      return;
    }

    const mixMode = groupDisplayMode() === "MIX";
    const hasIndividualCollection = mixMode && individualCollection !== null;
    const isVmgPercChannel = String(channel || "").toUpperCase() === "VMG_PERC";
    
    let minXValue = 999999
    let maxXValue = -999999
    
    let minYValue = 999999
    let maxYValue = -999999

    // MIX mode: build outlier-filtered tracks (same as standard TimeSeries) for Y scale and drawing
    type IndividualPathItem = { pathKey: string; points: TimeSeriesDataPoint[]; firstPoint: TimeSeriesDataPoint };
    let individualPathsOutlierFiltered: IndividualPathItem[] | null = null;

    if (hasIndividualCollection && individualCollection != undefined) {
      // Group-key (per maneuver, constant across its samples) computed here so the reactive colour mode
      // stays on the main thread; the O(paths × samples) sort + 2σ filter runs on a @nodets pool when the
      // caller passed precomputed paths, else inline via the identical shared builder (maneuverPathsBuild.ts).
      if (precomputedIndividualPaths) {
        individualPathsOutlierFiltered = precomputedIndividualPaths as unknown as IndividualPathItem[];
      } else {
        const colorName = tsColorMode();
        const groupKeys = individualCollection.map((vals) =>
          String(getGroupKeyFromItem(vals[0] as Record<string, unknown>, colorName))
        );
        individualPathsOutlierFiltered = buildGroupedIndividualPaths({
          individualCollection: individualCollection as any,
          channel,
          isVmgPercChannel,
          groupKeys,
        }) as unknown as IndividualPathItem[];
      }
      // Y scale from outlier-filtered tracks
      if (individualPathsOutlierFiltered.length > 0) {
        minYValue = 999999;
        maxYValue = -999999;
        individualPathsOutlierFiltered.forEach((m) => {
          m.points.forEach((d) => {
            const yValNum = Number((d as any)[channel]);
            if (Number.isFinite(yValNum) && Math.abs(yValNum) > 0) {
              if (yValNum > maxYValue) maxYValue = yValNum;
              if (yValNum < minYValue) minYValue = yValNum;
            }
          });
        });
      }
    }

    // ON mode: Y-axis from grouped data
    if (!hasIndividualCollection && collection != undefined) {
      for (let i = 0; i < collection.length; i++) {
        const vals = collection[i];
        for (let j = 0; j < vals.length; j++) {
          const d = vals[j];
          const yVal = (d as any)[channel];
          const yValNum = typeof yVal === 'number' ? yVal : parseFloat(String(yVal || 0));
          if (Math.abs(yValNum) > 0) {
            if (yValNum > maxYValue) maxYValue = yValNum;
            if (yValNum < minYValue) minYValue = yValNum;
          }
        }
      }
    }
    // Fallback if no valid Y yet (e.g. empty filtered paths)
    if (minYValue === 999999 && collection != undefined) {
      for (let i = 0; i < collection.length; i++) {
        const vals = collection[i];
        for (let j = 0; j < vals.length; j++) {
          const d = vals[j];
          const yVal = (d as any)[channel];
          const yValNum = typeof yVal === 'number' ? yVal : parseFloat(String(yVal || 0));
          if (Math.abs(yValNum) > 0) {
            if (yValNum > maxYValue) maxYValue = yValNum;
            if (yValNum < minYValue) minYValue = yValNum;
          }
        }
      }
    }
  
    if (collection != undefined) {
      // Ensure arrays exist for this index
      if (!timeArray[index]) {
        timeArray[index] = [];
      }
      if (!valArray[index]) {
        valArray[index] = [];
      }
      
      // Cache mode_x() value to avoid repeated function calls
      const useTimeForX = mode_x();
      const fill_eid = eidArray.length === 0;
      
      // Pre-allocate arrays for better performance
      const timeArr = timeArray[index];
      const valArr = valArray[index];
      
      // Safety check: ensure arrays are defined
      if (!timeArr || !valArr) {
        logError(`Arrays not initialized for index ${index}`);
        return;
      }
      
      // Process collection for timeArr/valArr and X range (Y range already set above)
      for (let i = 0; i < collection.length; i++) {
        const vals = collection[i];
        for (let j = 0; j < vals.length; j++) {
          const d = vals[j];
          
          // Calculate X value
          const bspVal = (d as any).bsp || 0;
          const xVal = useTimeForX ? parseFloat(String(d.time)) : parseFloat(String(bspVal));
          if (xVal > maxXValue) maxXValue = xVal;
          if (xVal < minXValue) minXValue = xVal;

          // Get Y value (channel value)
          const yVal = (d as any)[channel];
          const yValNum = typeof yVal === 'number' ? yVal : parseFloat(String(yVal || 0));

          // Push to arrays
          timeArr.push(useTimeForX ? d.time : bspVal);
          valArr.push(yValNum);

          // Fill event ID array only once
          if (fill_eid) {
            eidArray.push(d.event_id);
          }
        }
      }
    }

    // The reference line is drawn after this scale. Fold its values in first, or a baseline above
    // or below the runs is clipped off the chart.
    if (maneuverBaselineStore.baselineActive() && !maneuverBaselineStore.baselineLoading()) {
      const baselineY = baselineTimeseriesChannelExtent(maneuverBaselineStore.baselineTracks(), channel);
      if (baselineY) {
        if (minYValue === 999999 || maxYValue === -999999) {
          minYValue = baselineY.min;
          maxYValue = baselineY.max;
        } else {
          if (baselineY.min < minYValue) minYValue = baselineY.min;
          if (baselineY.max > maxYValue) maxYValue = baselineY.max;
        }
      }
    }
  
    maxYValue = maxYValue + ((maxYValue - minYValue) / 5)
    minYValue = minYValue - ((maxYValue - minYValue) / 15)
  
    if (maxYValue - minYValue < 1.5) {
      if (minYValue < 0.25 && minYValue > 0) {
        minYValue = -0.1
      }
    }
  
    //SETUP SCALES
    let yScale = d3.scaleLinear()
      .domain([minYValue, maxYValue])
      .range([chartHeight, 0]);
      
    const darkMode = isDark();

    let yAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(20, 0)")
      .call(d3.axisLeft(yScale).ticks(4).tickFormat((d: d3.NumberValue) => myTickFormat(Number(d))));

    // Style y-axis tick labels based on theme
    yAxis.selectAll("text")
      .style("fill", darkMode ? "#ffffff" : "#000000");
    
    let xScale = d3.scaleLinear()
      .domain([minXValue, maxXValue])
      .range([0, chartWidth]);
    
    let xAxis = chartbody.append("g")
      .attr("class", "axes")
      .attr("transform", "translate(20, " + chartHeight + ")")
      .call(d3.axisBottom(xScale).ticks(10).tickFormat((d: d3.NumberValue) => String(Number(d))))
    
    chartbody.append("text")
      .attr("class", "axis-label")
      .attr("text-anchor", "top")  
      .attr("transform", "translate(30,15)")  
      .attr("font-size", "14px")
      .attr("user-select", "none")
      .attr("pointer-events", "none")
      .attr("fill", darkMode ? "#ffffff" : "#000000")
      .text(channel.toUpperCase()) 
  
    chartbody
      .append("g")
      .attr("class", "grid")
      .attr("transform", "translate(20," + chartHeight + ")")
      .style("stroke-dasharray", "3,3")
      .call(make_x_gridlines(xScale).tickSize(-chartHeight).tickFormat(() => ""));

    chartbody
      .append("g")
      .attr("class", "grid")
      .attr("transform", "translate(20, 0)")
      .style("stroke-dasharray", "3,3")
      .call(make_y_gridlines(yScale).tickSize(-chartWidth).tickFormat(() => ""));
  
    // Declare before brush so updateChart (called on brush "end") can use them without TDZ
    let idleTimeout: ReturnType<typeof setTimeout> | null = null;
    function idled() { idleTimeout = null; }

    const brush = d3.brush()
      .extent( [ [0,0], [chartWidth,chartHeight] ] )
      .on("end", updateChart) 
  
    chartbody.append("g")
      .attr("class", "brush")
      .call(brush)
      .on("dblclick", refreshChart);

    // Mouse event handlers
    const mouseover = (event: MouseEvent, d: TimeSeriesDataPoint[]) => {
      const tooltipContent = getTooltipContent(d[0]); 
      const position = getToolTipPosition(event);

      setTooltip({
          visible: true,
          content: tooltipContent,
          x: position.x - 150,
          y: position.y
      });
    };

    const mousemove = (event: MouseEvent, d: TimeSeriesDataPoint[]) => {
      const tooltipContent = getTooltipContent(d[0]); 
      const position = getToolTipPosition(event);

      setTooltip({
          visible: true,
          content: tooltipContent,
          x: position.x - 150,
          y: position.y
      });
    };

    const mouseout = () => {
      setTooltip({
        visible: false,
        content: "",
        x: 0,
        y: 0
      });
    };

    const getTooltipContent = (point: TimeSeriesDataPoint): string => {
      if (!point) return "";

      // Join with table data by event_id; datetime comes pre-formatted in local time (datetimeLocal)
      const tableData = tabledata();
      const tableRow = tableData.find((row: any) => row.event_id === point.event_id);
      let dateString = "N/A";
      let timeString = "N/A";
      if (tableRow?.datetimeLocal) {
        const parts = tableRow.datetimeLocal.split(" ");
        dateString = parts[0] ?? "N/A";
        timeString = parts[1] ?? "N/A";
      }

      const isFleet = point.source_name != null && String(point.source_name).trim() !== "";
      const useTimeForX = mode_x();
      const xVal = useTimeForX ? point.time : ((point as any).bsp ?? 0);
      // X is always displayed as seconds (float), not formatted as date/time
      const xDisplay = typeof xVal === "number" && !isNaN(xVal)
        ? String(parseFloat(xVal.toFixed(2)))
        : String(xVal);
      const yVal = (point as any)[channel];
      const yNum = typeof yVal === "number" ? yVal : parseFloat(String(yVal || 0));
      const yDisplay = !isNaN(yNum) ? (Math.round(yNum * 10) / 10).toFixed(1) : String(yVal ?? "");

      let tooltipRows = `
              <tr><td>DATE</td><td>${dateString}</td></tr>
              <tr><td>TIME</td><td>${timeString}</td></tr>`;
      if (isFleet) {
        tooltipRows += `
              <tr><td>SOURCE</td><td>${String(point.source_name)}</td></tr>`;
      }
      tooltipRows += `
              <tr><td>TIME (SEC)</td><td>${xDisplay}</td></tr>
              <tr><td>${String(channel || "").toUpperCase()}</td><td>${yDisplay}</td></tr>`;

      return `<table class='table-striped'>${tooltipRows}</table>`;
    };
    
    const click = function(event: MouseEvent, d: unknown) {
      event.stopPropagation();
      const maneuver = d as { firstPoint?: TimeSeriesDataPoint };
      const firstPoint = maneuver?.firstPoint;
      if (!firstPoint) return;
      const pathGroupKey = getGroupKeyFromItem(firstPoint, tsColorMode());
      setSelectedGroupKeys((prev) =>
        prev.includes(pathGroupKey) ? prev.filter((k) => k !== pathGroupKey) : [...prev, pathGroupKey]
      );
      setTriggerSelection(true);
    };

    // DRAW CONTENT
    // Use curveCatmullRom for smoother interpolation - provides very smooth curves
    // Alpha of 0.5 gives a good balance between smoothness and following the data points
    const toLinePath = d3.line<TimeSeriesDataPoint>()
      .x(d => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)))
      .y(d => yScale((d as any)[channel] || 0))
      .curve(d3.curveCatmullRom.alpha(0.5)); // Smooth Catmull-Rom spline with moderate alpha
    
    /**
     * ONE baseline line per chart, both tacks together — the stored per-maneuver timeseries is already
     * side-normalized by the pipeline, so port and starboard traces are directly averageable.
     *
     * Draws nothing when no averaged trace covers this chart's channel: a channel the member payload
     * does not carry has no reference, and a flat line at zero would invent one.
     */
    function drawBaselineTimeseriesLine(): void {
      chartbody.selectAll(".linePath-baseline").remove();
      if (!maneuverBaselineStore.baselineActive()) { return; }
      if (maneuverBaselineStore.baselineLoading()) { return; }
      const tracks = maneuverBaselineStore.baselineTracks();
      if (!tracks) { return; }

      const points = baselineTimeseriesForChannel(tracks, channel);
      if (points.length < 2) { return; }

      const validPoints = points
        .map((p) => {
          const val = readBaselineTimeseriesValue(p, channel);
          if (val === null) { return null; }
          return { ...p, time: p.time, [channel]: val } as TimeSeriesDataPoint;
        })
        .filter((p): p is TimeSeriesDataPoint => p !== null);
      if (validPoints.length < 2) { return; }

      chartbody
        .append("path")
        .attr("class", "linePath-baseline")
        .style("stroke", maneuverBaselineStore.baselineColor())
        .style("fill", "none")
        .attr("transform", "translate(20, 0)")
        .attr("d", toLinePath(validPoints));
    }

    function drawPaths() {
      chartbody.selectAll(".linePath").attr("d", (d: unknown) => {
        const m = d as { points?: TimeSeriesDataPoint[] } | TimeSeriesDataPoint[];
        if (m && typeof m === "object" && "points" in m && Array.isArray(m.points)) {
          return toLinePath(m.points);
        }
        return toLinePath(m as TimeSeriesDataPoint[]);
      });
    }

    if (collection !== undefined) {
      chartbody.select(".brush").call(brush.move, null);
      chartbody.selectAll(".linePath").remove();
      chartbody.selectAll(".linePath-individual").remove();
      chartbody.selectAll(".linePath-outline").remove();
      chartbody.selectAll(".hover-circle").remove();
  
      interface ManeuverPath {
        eventId?: number;
        pathKey: string;
        points: TimeSeriesDataPoint[];
        firstPoint: TimeSeriesDataPoint;
      }
      let maneuverPaths: ManeuverPath[] = [];

      // MIX MODE: Draw individual tracks (outlier-filtered) if provided. ON mode: only grouped (individual already removed above).
      if (hasIndividualCollection && individualPathsOutlierFiltered != null && individualPathsOutlierFiltered.length > 0) {
        const individualPaths = individualPathsOutlierFiltered as ManeuverPath[];

        const individualSelection = chartbody.selectAll(".linePath-individual")
          .data(individualPaths, (d: ManeuverPath) => d.pathKey);

        individualSelection
          .enter()
          .append("path")
          .attr("class", "linePath-individual")
          .merge(individualSelection)
          .each(function(d: ManeuverPath) {
            const node = this as SVGPathElement;
            // MIX individual tracks are always coloured by their group (matches updateSelection, which in
            // MIX colours individual tracks regardless of selection). Previously this drew them "lightgrey"
            // and relied on a follow-up updateSelection() pass to colour them — an ordering the synchronous
            // draw guaranteed but the async (pooled) draw can miss, leaving initially-visible charts grey
            // until re-rendered on scroll. Colour directly so it's correct on first paint.
            const strokeColor = getColor(d.firstPoint);
            node.style.setProperty("transition", "none", "important");
            node.style.setProperty("stroke", strokeColor, "important");
            node.style.setProperty("stroke-width", "0.5", "important");
            node.style.setProperty("stroke-opacity", "0.3", "important");
          })
          .style("fill", "none")
          .style("pointer-events", "none")
          .attr("transform", "translate(20, 0)")
          .attr("d", (d: ManeuverPath) => toLinePath(d.points));

        individualSelection.exit().remove();
      } else {
        // ON mode: ensure no individual tracks remain (e.g. after switching from MIX)
        chartbody.selectAll(".linePath-individual").remove();
      }

      // Now draw grouped tracks (always drawn)
      collection.forEach((vals: TimeSeriesDataPoint[], i: number) => {
        const points = [...vals].sort((a, b) => a.time - b.time);
        const filteredPoints: TimeSeriesDataPoint[] = [];
        let prevPoint: TimeSeriesDataPoint | null = null;
        points.forEach(d => {
          if (prevPoint === null) {
            filteredPoints.push(d);
            prevPoint = d;
          } else {
            const seconds = Math.abs(prevPoint.time - d.time);
            if (seconds < 5) {
              filteredPoints.push(d);
              prevPoint = d;
            }
          }
        });
        if (filteredPoints.length > 1) {
          const gv = getGroupKeyFromItem(filteredPoints[0] as Record<string, unknown>, tsColorMode());
          maneuverPaths.push({
            pathKey: `group_${String(gv)}_${i}`,
            points: filteredPoints,
            firstPoint: filteredPoints[0]
          });
        }
      });

      const orderedPaths = [...maneuverPaths];
      const linePathsSelection = chartbody.selectAll(".linePath")
        .data(orderedPaths, (d: ManeuverPath) => d.pathKey);

      const hasGroupSelection = selectedGroupKeys().length > 0;
      const mixModeDraw = groupDisplayMode() === 'MIX';
      
      linePathsSelection
        .enter()
        .append("path")
        .attr("class", "linePath solid_line")
        .merge(linePathsSelection)
        .each(function(d: ManeuverPath) {
          const node = this as SVGPathElement;
          const pathGroupKey = getGroupKeyFromItem(d.firstPoint, tsColorMode());
          const isSelected = hasGroupSelection && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
          const strokeColor = isSelected ? getColor(d.firstPoint) : (hasGroupSelection ? (mixModeDraw ? getColor(d.firstPoint) : "lightgrey") : getColor(d.firstPoint));
          const strokeWidth = mixModeDraw ? 2 : (isSelected ? 3 : (hasGroupSelection ? 0.5 : 1));
          const strokeOpacity = isSelected ? 1 : (hasGroupSelection ? 0.2 : 1);
          node.style.setProperty("transition", "none", "important");
          node.style.setProperty("stroke", strokeColor, "important");
          node.style.setProperty("stroke-width", String(strokeWidth), "important");
          node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
        })
        .style("fill", "none")
        .style("pointer-events", "none")
        .attr("transform", "translate(20, 0)")
        .attr("d", (d: ManeuverPath) => toLinePath(d.points));

      drawBaselineTimeseriesLine();

      // Remove any existing outlines (we don't want outlines for selected paths)
      chartbody.selectAll(".linePath-outline").remove();

      // Add invisible circles for hover detection and tooltips (only for grouped tracks)
      chartbody.selectAll(".hover-circle").remove();
      orderedPaths.forEach((maneuver: ManeuverPath) => {
        const pathGroupKey = getGroupKeyFromItem(maneuver.firstPoint, tsColorMode());
        const isSelected = hasGroupSelection && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
        const noPointerEvents = mixModeDraw && !isSelected;
        // Use pathKey for CSS class/selectors (sanitized to avoid invalid selectors)
        const eventKey = String(maneuver.pathKey).replace(/[^a-zA-Z0-9_-]/g, "_");

        const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
          .data(maneuver.points);
        
        const circlesMerge = circles.enter()
          .append("circle")
          .attr("class", `hover-circle hover-circle-${eventKey}`)
          .merge(circles)
          .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20) // Add 20 for transform offset
          .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
          .attr("r", 10) // Larger hit area for grouped (fewer lines); radius 10 = 20px diameter
          .style("fill", "transparent")
          .style("stroke", "none")
          .style("pointer-events", noPointerEvents ? "none" : "all")
          .style("cursor", noPointerEvents ? "default" : "pointer");
        if (!noPointerEvents) {
          circlesMerge
            .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
            .on("mouseout", mouseout)
            .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
            .on("click", (event: MouseEvent) => click(event, maneuver));
        } else {
          circlesMerge.on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
        }
        
        circles.exit().remove();
      });
      
      // Store mouse handlers for this chart so they can be reattached when chart becomes visible again
      storedMouseHandlers.set(wrapperId, {
        mouseover: mouseover,
        mouseout: mouseout,
        mousemove: mousemove,
        click: click
      });
  
      linePathsSelection.exit().remove();
    }

    function updateChart(event: d3.D3BrushEvent<unknown>) {
      const extent = event.selection; // Brush selection
  
      if (!extent) {
        if (!idleTimeout) {
            idleTimeout = setTimeout(idled, 150); // Idle timeout logic
        }

        // Reset scales to the full domain
        xScale.domain([minXValue, maxXValue]);
        yScale.domain([minYValue, maxYValue]);

        // Update axes
        xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));
        yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));

        setTooltip({
          visible: false,
          content: "",
          x: 0,
          y: 0
        });
      } else {
        // Extract brush extent and convert to domain ranges (extent is [[x0,y0],[x1,y1]] in pixels)
        const sel = extent as [[number, number], [number, number]];
        const [[x0, y0], [x1, y1]] = sel;
        const minx = xScale.invert(x0);
        const maxx = xScale.invert(x1);
        const maxy = yScale.invert(y0); // Note: y-invert is reversed because the origin is top-left
        const miny = yScale.invert(y1);

        // Update scales based on brush extent
        xScale.domain([minx, maxx]);
        yScale.domain([miny, maxy]);

        interface ManeuverPathBrush {
          eventId?: number;
          pathKey: string;
          points: TimeSeriesDataPoint[];
          firstPoint: TimeSeriesDataPoint;
        }
        let maneuverPaths: ManeuverPathBrush[] = [];

        // Grouped: filter each series by extent, one path per series
        collection.forEach((vals: TimeSeriesDataPoint[], i: number) => {
          const inExtent = (d: TimeSeriesDataPoint) => {
            if (mode_x()) {
              return d.time > minx && d.time < maxx && (d as any)[channel] > miny && (d as any)[channel] < maxy;
            }
            return (d as any).bsp > minx && (d as any).bsp < maxx && (d as any)[channel] > miny && (d as any)[channel] < maxy;
          };
          const points = vals.filter(inExtent).sort((a, b) => a.time - b.time);
          if (points.length > 1) {
            const gv = getGroupKeyFromItem(points[0] as Record<string, unknown>, tsColorMode());
            maneuverPaths.push({
              pathKey: `group_${String(gv)}_${i}`,
              points,
              firstPoint: points[0]
            });
          }
        });

        if (maneuverPaths.length > 0) {
          // Clear the brush selection
          chartbody.select(".brush").call(brush.move, null);
          chartbody.selectAll(".linePath").remove();
          chartbody.selectAll(".linePath-individual").remove();
          chartbody.selectAll(".linePath-outline").remove();
          chartbody.selectAll(".hover-circle").remove();

          // Recalculate x and y domains based on filtered data
          const allXValues = maneuverPaths.flatMap(m => m.points.map(d => mode_x() ? d.time : ((d as any).bsp || 0)));
          const newMinX = d3.min(allXValues);
          const newMaxX = d3.max(allXValues);
          // MIX mode: Y domain from outlier-filtered individual tracks in extent; ON mode: from grouped
          let newMinY: number;
          let newMaxY: number;
          if (hasIndividualCollection && individualPathsOutlierFiltered != null && individualPathsOutlierFiltered.length > 0) {
            const individualYInExtent: number[] = individualPathsOutlierFiltered.flatMap((m) =>
              m.points.filter((d: TimeSeriesDataPoint) => {
                const x = mode_x() ? d.time : ((d as any).bsp ?? 0);
                return x >= (newMinX ?? 0) && x <= (newMaxX ?? 0);
              }).map((d: TimeSeriesDataPoint) => {
                const v = (d as any)[channel];
                return typeof v === 'number' && !isNaN(v) ? v : parseFloat(String(v || 0)) || 0;
              })
            );
            newMinY = individualYInExtent.length > 0 ? (d3.min(individualYInExtent) ?? 0) : 0;
            newMaxY = individualYInExtent.length > 0 ? (d3.max(individualYInExtent) ?? 0) : 0;
          } else if (!hasIndividualCollection) {
            const allYValues = maneuverPaths.flatMap(m => m.points.map(d => (d as any)[channel] || 0));
            newMinY = d3.min(allYValues) ?? 0;
            newMaxY = d3.max(allYValues) ?? 0;
          } else {
            newMinY = minYValue;
            newMaxY = maxYValue;
          }
  
          xScale.domain([newMinX, newMaxX]);
          yScale.domain([newMinY, newMaxY]);
  
          // Update axes with smooth transitions
          xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));
          yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));
  
          // MIX MODE: Redraw individual tracks (outlier-filtered, then by extent)
          if (hasIndividualCollection && individualPathsOutlierFiltered != null && individualPathsOutlierFiltered.length > 0) {
            const inExtent = (d: TimeSeriesDataPoint) => {
              const x = mode_x() ? d.time : ((d as any).bsp ?? 0);
              return x >= (newMinX ?? 0) && x <= (newMaxX ?? 0);
            };
            const individualPathsBrush = individualPathsOutlierFiltered
              .map((m) => {
                const points = m.points.filter(inExtent).sort((a, b) => a.time - b.time);
                return points.length > 1 ? { pathKey: m.pathKey, points, firstPoint: points[0] } : null;
              })
              .filter((x): x is NonNullable<typeof x> => x != null);

            const hasGroupSelectionBrush = selectedGroupKeys().length > 0;
            const individualBrushSelection = chartbody.selectAll(".linePath-individual")
              .data(individualPathsBrush, (d: any) => d.pathKey);

            individualBrushSelection
              .enter()
              .append("path")
              .attr("class", "linePath-individual")
              .merge(individualBrushSelection)
              .each(function(d: any) {
                const node = this as SVGPathElement;
                const pathGroupKey = getGroupKeyFromItem(d.firstPoint, tsColorMode());
                const isInSelectedGroup = hasGroupSelectionBrush && selectedGroupKeys().some((k: string | number) => groupKeyEquals(k, pathGroupKey));
                const strokeColor = isInSelectedGroup ? getColor(d.firstPoint) : "lightgrey";
                node.style.setProperty("transition", "none", "important");
                node.style.setProperty("stroke", strokeColor, "important");
                node.style.setProperty("stroke-width", "0.5", "important");
                node.style.setProperty("stroke-opacity", "0.3", "important");
              })
              .style("fill", "none")
              .style("pointer-events", "none")
              .attr("transform", "translate(20, 0)")
              .attr("d", (d: any) => toLinePath(d.points));

            individualBrushSelection.exit().remove();
          }

          // Now draw grouped tracks
          const orderedPaths = [...maneuverPaths];
          const linePathsSelection = chartbody.selectAll(".linePath")
              .data(orderedPaths, (d: ManeuverPathBrush) => d.pathKey);
  
          const hasGroupSelectionBrush = selectedGroupKeys().length > 0;
          const mixModeBrushStroke = groupDisplayMode() === 'MIX';
          
          linePathsSelection
              .enter()
              .append("path")
              .attr("class", (d: ManeuverPathBrush) => `linePath solid_line`)
              .merge(linePathsSelection)
              .each(function(d: ManeuverPathBrush) {
                const node = this as SVGPathElement;
                const pathGroupKey = getGroupKeyFromItem(d.firstPoint, tsColorMode());
                const isSelected = hasGroupSelectionBrush && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
                const strokeColor = isSelected ? getColor(d.firstPoint) : (hasGroupSelectionBrush ? (mixModeBrushStroke ? getColor(d.firstPoint) : "lightgrey") : getColor(d.firstPoint));
                const strokeWidth = mixModeBrushStroke ? 2 : (isSelected ? 3 : (hasGroupSelectionBrush ? 0.5 : 1));
                const strokeOpacity = isSelected ? 1 : (hasGroupSelectionBrush ? 0.2 : 1);
                node.style.setProperty("transition", "none", "important");
                node.style.setProperty("stroke", strokeColor, "important");
                node.style.setProperty("stroke-width", String(strokeWidth), "important");
                node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
              })
              .style("fill", "none")
              .style("pointer-events", "none")
              .attr("transform", "translate(20, 0)")
              .attr("d", (d: ManeuverPathBrush) => toLinePath(d.points));
          
          chartbody.selectAll(".linePath-outline").remove();
          chartbody.selectAll(".hover-circle").remove();
          const mixModeBrush = groupDisplayMode() === 'MIX';
          orderedPaths.forEach((maneuver: ManeuverPathBrush) => {
            const pathGroupKey = getGroupKeyFromItem(maneuver.firstPoint, tsColorMode());
            const isSelected = hasGroupSelectionBrush && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
            const noPointerEvents = mixModeBrush && !isSelected;
            const eventKey = String(maneuver.pathKey).replace(/[^a-zA-Z0-9_-]/g, "_");
            const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
              .data(maneuver.points);
            
            const circlesMerge = circles.enter()
              .append("circle")
              .attr("class", `hover-circle hover-circle-${eventKey}`)
              .merge(circles)
              .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20)
              .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
              .attr("r", 10) // Larger hit area for grouped (fewer lines)
              .style("fill", "transparent")
              .style("stroke", "none")
              .style("pointer-events", noPointerEvents ? "none" : "all")
              .style("cursor", noPointerEvents ? "default" : "pointer");
            if (!noPointerEvents) {
              circlesMerge
                .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
                .on("mouseout", mouseout)
                .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
                .on("click", (event: MouseEvent) => click(event, maneuver));
            } else {
              circlesMerge.on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
            }
            
            circles.exit().remove();
          });
  
          linePathsSelection.exit().remove();
        }          
      }
    }
  
    function refreshChart(): void {
      if (!idleTimeout) {
        idleTimeout = setTimeout(idled, 150);
        return;
      }

      // Update the scales and redraw the axes
      yScale.domain([minYValue, maxYValue]);
      yAxis.transition().duration(1000).call(d3.axisLeft(yScale).ticks(4));

      xScale.domain([minXValue, maxXValue]);
      xAxis.transition().duration(1000).call(d3.axisBottom(xScale).ticks(10));

      if (collection !== undefined) {
        chartbody.select(".brush").call(brush.move, null); // Clear brush selection
        chartbody.selectAll(".linePath").remove();
        chartbody.selectAll(".linePath-individual").remove();
        chartbody.selectAll(".linePath-outline").remove();
        chartbody.selectAll(".hover-circle").remove();

        interface ManeuverPathRefresh {
          eventId?: number;
          pathKey: string;
          points: TimeSeriesDataPoint[];
          firstPoint: TimeSeriesDataPoint;
        }
        
        // MIX MODE: Draw individual tracks (outlier-filtered) first if needed
        if (hasIndividualCollection && individualPathsOutlierFiltered != null && individualPathsOutlierFiltered.length > 0) {
          const individualPathsRefresh = individualPathsOutlierFiltered as ManeuverPathRefresh[];

          const hasGroupSelectionRefresh = selectedGroupKeys().length > 0;
          const individualRefreshSelection = chartbody.selectAll(".linePath-individual")
            .data(individualPathsRefresh, (d: ManeuverPathRefresh) => d.pathKey);

          individualRefreshSelection
            .enter()
            .append("path")
            .attr("class", "linePath-individual")
            .merge(individualRefreshSelection)
            .each(function(d: ManeuverPathRefresh) {
              const node = this as SVGPathElement;
              const pathGroupKey = getGroupKeyFromItem(d.firstPoint, tsColorMode());
              const isInSelectedGroup = hasGroupSelectionRefresh && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
              const strokeColor = isInSelectedGroup ? getColor(d.firstPoint) : "lightgrey";
              node.style.setProperty("transition", "none", "important");
              node.style.setProperty("stroke", strokeColor, "important");
              node.style.setProperty("stroke-width", "0.5", "important");
              node.style.setProperty("stroke-opacity", "0.3", "important");
            })
            .style("fill", "none")
            .style("pointer-events", "none")
            .attr("transform", "translate(20, 0)")
            .attr("d", (d: ManeuverPathRefresh) => toLinePath(d.points));

          individualRefreshSelection.exit().remove();
        }

        // Now draw grouped tracks
        let maneuverPaths: ManeuverPathRefresh[] = [];
        collection.forEach((vals: TimeSeriesDataPoint[], i: number) => {
          const points = [...vals].sort((a, b) => a.time - b.time);
          const filteredPoints: TimeSeriesDataPoint[] = [];
          let prevPoint: TimeSeriesDataPoint | null = null;
          points.forEach(d => {
            if (prevPoint === null) {
              filteredPoints.push(d);
              prevPoint = d;
            } else {
              const seconds = Math.abs(prevPoint.time - d.time);
              if (seconds < 5) {
                filteredPoints.push(d);
                prevPoint = d;
              }
            }
          });
          if (filteredPoints.length > 1) {
            const gv = getGroupKeyFromItem(filteredPoints[0] as Record<string, unknown>, tsColorMode());
            maneuverPaths.push({
              pathKey: `group_${String(gv)}_${i}`,
              points: filteredPoints,
              firstPoint: filteredPoints[0]
            });
          }
        });

        const orderedPaths = [...maneuverPaths];
        const linePathsSelection = chartbody.selectAll(".linePath")
          .data(orderedPaths, (d: ManeuverPathRefresh) => d.pathKey);

        const hasGroupSelectionRefresh = selectedGroupKeys().length > 0;
        const mixModeRefreshStroke = groupDisplayMode() === 'MIX';
        
        linePathsSelection
          .enter()
          .append("path")
          .attr("class", (d: ManeuverPathRefresh) => `linePath solid_line`)
          .merge(linePathsSelection)
          .each(function(d: ManeuverPathRefresh) {
            const node = this as SVGPathElement;
            const pathGroupKey = getGroupKeyFromItem(d.firstPoint, tsColorMode());
            const isSelected = hasGroupSelectionRefresh && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
            const strokeColor = isSelected ? getColor(d.firstPoint) : (hasGroupSelectionRefresh ? (mixModeRefreshStroke ? getColor(d.firstPoint) : "lightgrey") : getColor(d.firstPoint));
            const strokeWidth = mixModeRefreshStroke ? 2 : (isSelected ? 3 : (hasGroupSelectionRefresh ? 0.5 : 1));
            const strokeOpacity = isSelected ? 1 : (hasGroupSelectionRefresh ? 0.2 : 1);
            node.style.setProperty("transition", "none", "important");
            node.style.setProperty("stroke", strokeColor, "important");
            node.style.setProperty("stroke-width", String(strokeWidth), "important");
            node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
          })
          .style("fill", "none")
          .style("pointer-events", "none")
          .attr("transform", "translate(20, 0)")
          .attr("d", (d: ManeuverPathRefresh) => toLinePath(d.points));
        
        chartbody.selectAll(".linePath-outline").remove();
        chartbody.selectAll(".hover-circle").remove();
        const mixModeRefresh = groupDisplayMode() === 'MIX';
        orderedPaths.forEach((maneuver: ManeuverPathRefresh) => {
          const pathGroupKey = getGroupKeyFromItem(maneuver.firstPoint, tsColorMode());
          const isSelected = hasGroupSelectionRefresh && selectedGroupKeys().some(k => groupKeyEquals(k, pathGroupKey));
          const noPointerEvents = mixModeRefresh && !isSelected;
          const eventKey = String(maneuver.pathKey).replace(/[^a-zA-Z0-9_-]/g, "_");
          const circles = chartbody.selectAll(`.hover-circle-${eventKey}`)
            .data(maneuver.points);
          
          const circlesMerge = circles.enter()
            .append("circle")
            .attr("class", `hover-circle hover-circle-${eventKey}`)
            .merge(circles)
            .attr("cx", (d: TimeSeriesDataPoint) => (mode_x() ? xScale(d.time) : xScale((d as any).bsp || 0)) + 20)
            .attr("cy", (d: TimeSeriesDataPoint) => yScale((d as any)[channel] || 0))
            .attr("r", 10) // Larger hit area for grouped (fewer lines)
            .style("fill", "transparent")
            .style("stroke", "none")
            .style("pointer-events", noPointerEvents ? "none" : "all")
            .style("cursor", noPointerEvents ? "default" : "pointer");
          if (!noPointerEvents) {
            circlesMerge
              .on("mouseover", (event: MouseEvent, d: TimeSeriesDataPoint) => mouseover(event, [d]))
              .on("mouseout", mouseout)
              .on("mousemove", (event: MouseEvent, d: TimeSeriesDataPoint) => mousemove(event, [d]))
              .on("click", (event: MouseEvent) => click(event, maneuver));
          } else {
            circlesMerge.on("mouseover", null).on("mouseout", null).on("mousemove", null).on("click", null);
          }
          
          circles.exit().remove();
        });
    
        linePathsSelection.exit().remove();
        drawPaths();
      }
    }

    drawPaths()
  }

  function updateSelection() {
    updateMinMaxRanges();
    const hasGroupSelection = selectedGroupKeys().length > 0;
    const mixModeUpdateSel = groupDisplayMode() === "MIX";
    const selected = highlightedEventIds();
    const hasEventSelection = selected.length > 0;

    requestAnimationFrame(() => {
      const root = containerRef;
      const svgs = root ? Array.from(root.querySelectorAll("svg.ts")) : [];

      for (const svg of svgs) {
        const chartBody = d3.select(svg).select(":scope > g");
        if (chartBody.empty()) continue;

        chartBody.selectAll(".linePath, .linePath-individual").each(function (d: unknown) {
          const node = this as SVGPathElement;
          const maneuver = d as { firstPoint?: TimeSeriesDataPoint; points?: TimeSeriesDataPoint[] };
          const firstPoint = maneuver?.firstPoint ?? (maneuver?.points && maneuver.points?.[0]) ?? null;
          if (!node || !firstPoint) return;
          // Individual MIX tracks: always coloured by group and always faint (match drawPaths). The grouped
          // (average) tracks carry the selection emphasis. Applying the same styling the draw does keeps the
          // final look independent of draw/updateSelection ordering — the async (pooled) draw can run either
          // side of this pass, and previously this uniform block overwrote the individual tracks' 0.3 opacity.
          if (node.classList.contains("linePath-individual")) {
            node.style.setProperty("transition", "none", "important");
            node.style.setProperty("stroke", getColor(firstPoint), "important");
            node.style.setProperty("stroke-width", "0.5", "important");
            node.style.setProperty("stroke-opacity", "0.3", "important");
            return;
          }
          const pathGroupKey = getGroupKeyFromItem(firstPoint, tsColorMode());
          const isSelected = hasGroupSelection && selectedGroupKeys().some((k) => groupKeyEquals(k, pathGroupKey));
          const strokeColor = isSelected
            ? getColor(firstPoint)
            : hasGroupSelection
              ? mixModeUpdateSel
                ? getColor(firstPoint)
                : "lightgrey"
              : getColor(firstPoint);
          const strokeWidth = mixModeUpdateSel ? 2 : (isSelected ? 3 : hasGroupSelection ? 0.5 : 1);
          const strokeOpacity = isSelected ? 1 : hasGroupSelection ? 0.2 : 1;
          node.style.setProperty("transition", "none", "important");
          node.style.setProperty("stroke", strokeColor, "important");
          node.style.setProperty("stroke-width", String(strokeWidth), "important");
          node.style.setProperty("stroke-opacity", String(strokeOpacity), "important");
        });
      }

      setTriggerSelection(false);

      // Banner/hasSelection reflects MANUAL selection only; the live highlight layer must not set it.
      // Grouped views hold the manual selection in selectedGroupKeys (not event IDs), so count those
      // too - otherwise this pass (which runs right after a group click sets triggerSelection) clears
      // hasSelection and leaves the selection banner up but empty.
      if (selectedEvents().length > 0 || selectedGroupKeys().length > 0) {
        setHasSelection(true);
      } else {
        const hasBrushRange = selectedRange() && selectedRange().length > 0;
        if (!hasBrushRange) {
          setHasSelection(false);
          setSelection([]);
        }
      }
    });
  }

  // Event types to target for a tag write - a single selected type, else all maneuver types.
  const maneuverEventTypes = (): string[] => {
    const currentEventType = eventType();
    return currentEventType ? [currentEventType] : MANEUVER_EVENT_TYPES;
  };

  // Shared post-write handling for GRADE and STATE updates.
  const afterTagUpdate = (): void => {
    // Trigger parent component to refetch data
    if (props.onDataUpdate && typeof props.onDataUpdate === 'function') {
      props.onDataUpdate();
    } else {
      // Fallback: trigger update
      setTriggerUpdate(true);
    }
  };

  // Function to perform the GRADE update
  const performGradeUpdate = async (gradeValue: number, selected: number[]): Promise<void> => {
    // Get required values from persistent store
    const { selectedClassName, selectedProjectId } = persistantStore;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    if (!className || !projectId) {
      logError('Cannot update GRADE: missing class_name or project_id');
      return;
    }

    const eventTypes = maneuverEventTypes();

    try {
      // Call admin API to update GRADE for selected events
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: selected,
        event_types: eventTypes,
        key: 'GRADE',
        value: gradeValue
      });
      
      if (response.success) {
        debug(`Successfully updated GRADE to ${gradeValue} for ${selected.length} event(s)`);
        
        // Grade update in HuniDB will be implemented when available
        // Currently grade updates are handled on next data fetch
        
        // Update grade filter to include the newly graded value
        const currentGrades = selectedGradesManeuvers();
        const gradeValueStr = String(gradeValue);
        if (!currentGrades.includes(gradeValueStr)) {
          setSelectedGradesManeuvers([...currentGrades, gradeValueStr]);
          debug(`Added grade ${gradeValue} to selectedGrades filter`);
        }

        afterTagUpdate();
      } else {
        logError('Failed to update GRADE:', response.message || 'Unknown error');
      }
    } catch (error: any) {
      logError('Error updating GRADE:', error);
    }
  };

  /**
   * Rebuild every chart that already has data: each loaded multi-group section, or the single series.
   * Shared by the resize handler (width change) and the baseline effect below.
   */
  function rebuildLoadedCharts() {
    if (isMultiGroupMode()) {
      const map = groupSeriesState();
      for (const k of Object.keys(map)) {
        const ent = map[k];
        if (ent.status !== "loaded" || !ent.data?.length) { continue; }
        const m = groupPlotMounts.get(k);
        if (m) {
          buildTimeSeries(ent.data, {
            mountEl: m,
            groupKey: k,
            chartsOrder: ent.charts ?? [],
            channelOrderStored: ent.channelOrder ?? [],
          });
        }
      }
    } else if (tsdata().length > 0) {
      buildTimeSeries(tsdata());
    }
  }

  /**
   * The averaged baseline traces are fetched, so they arrive after the charts have drawn. Rebuild
   * then, so the reference appears without waiting for an unrelated redraw. Deferred: on mount the
   * charts are built by their own path.
   */
  createEffect(
    on(
      () => [
        maneuverBaselineStore.baselineActive(),
        maneuverBaselineStore.baselineTracks(),
        maneuverBaselineStore.baselineLoading(),
      ],
      () => {
        if (maneuverBaselineStore.baselineLoading()) { return; }
        untrack(() => rebuildLoadedCharts());
      },
      { defer: true }
    )
  );

  // Function to calculate and set container height
  const updateContainerHeight = () => {
    if (!containerRef) return;

    // Solo ManeuverWindow: scroll viewport height is owned by setupMediaContainerScaling
    // (applyManeuverWindowScrollViewport in global.ts) — see docs/frontend/maneuver-window-scaling.md.
    if (props.instanceId) {
      containerRef.style.removeProperty("min-height");
      containerRef.style.removeProperty("height");
      containerRef.style.removeProperty("max-height");
      const tsArea = document.querySelector(`[data-maneuver-ts-area="${props.instanceId}"]`);
      if (tsArea instanceof HTMLElement) {
        tsArea.style.removeProperty("min-height");
        tsArea.style.removeProperty("height");
      }
      lastAppliedLayoutHeightPx = 0;
      return;
    }

    // Find the media-container parent that has the scale transform
    let parent = containerRef.parentElement;
    let mediaContainer: HTMLElement | null = null;
    while (parent) {
      if (parent.id === 'media-container') {
        mediaContainer = parent;
        break;
      }
      parent = parent.parentElement;
    }
    
    // Get the scale factor from CSS custom property or calculate from transform
    let scaleFactor = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1;
    
    // If we found the media-container, try to get scale from its computed transform
    if (mediaContainer) {
      const transform = getComputedStyle(mediaContainer).transform;
      if (transform && transform !== 'none') {
        // Parse matrix scale: matrix(scaleX, skewY, skewX, scaleY, translateX, translateY)
        const matrix = transform.match(/matrix\(([^)]+)\)/);
        if (matrix) {
          const values = matrix[1].split(',').map(v => parseFloat(v.trim()));
          if (values.length >= 4) {
            // scaleX is the first value in the matrix
            scaleFactor = values[0];
          }
        }
      }
    }
    
    // The shared color legend row sits above this view on every host; it is the only legend
    // now, and it is measured (never a constant) because it rewraps with the color mode.
    const legendHeight = maneuverColorLegendRenderedPx();
    
    // In split view use the same height logic as fullscreen: fill the available space (match setupMediaContainerScaling formula)
    const splitPanel = mediaContainer?.closest('.split-panel') as HTMLElement | null;
    const headerHeight = 60; // Account for header (only in fullscreen); matches maneuvers-page reserve in global.ts
    const padding = 20; // Some padding for spacing
    const inManeuverWindow = Boolean(props.instanceId);
    let availableViewportHeight: number;
    if (splitPanel) {
      // Use panel height minus 60px reserve (same as setupMediaContainerScaling for maneuvers-page) so we fill the panel regardless of timing
      availableViewportHeight = Math.max(200, splitPanel.clientHeight - 60);
    } else if (inManeuverWindow) {
      const mainContent = document.getElementById("main-content");
      availableViewportHeight = Math.max(
        200,
        mainContent?.clientHeight ?? window.innerHeight
      );
    } else {
      availableViewportHeight = window.innerHeight;
    }
    const effectiveHeaderHeight = splitPanel || inManeuverWindow ? 0 : headerHeight;
    const desiredVisibleHeight = availableViewportHeight - effectiveHeaderHeight - legendHeight - padding;
    
    // When content is scaled down, we need MORE layout height to achieve the desired visible height
    // Formula: layoutHeight = desiredVisibleHeight / scaleFactor
    const layoutHeight = desiredVisibleHeight / scaleFactor;
    
    // Debug logging
    debug('Height calculation:', {
      windowInnerHeight: window.innerHeight,
      scaleFactorFromCSS: parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--scale-factor')) || 1,
      scaleFactorFromTransform: scaleFactor,
      headerHeight,
      legendHeight,
      padding,
      desiredVisibleHeight,
      layoutHeight,
      finalHeight: Math.max(400, layoutHeight),
      containerStyleHeight: containerRef.style.height,
      containerComputedHeight: getComputedStyle(containerRef).height
    });
    
    const layoutPx = Math.max(400, layoutHeight);
    const isFullPageMode = !splitPanel && !props.instanceId;
    const chartStackHeightPx = isFullPageMode ? getRenderedChartStackHeight(scaleFactor) : 0;
    const appliedHeightPx = isFullPageMode ? Math.max(layoutPx, chartStackHeightPx) : layoutPx;
    if (lastAppliedLayoutHeightPx > 0 && Math.abs(appliedHeightPx - lastAppliedLayoutHeightPx) < 3) {
      return;
    }
    lastAppliedLayoutHeightPx = appliedHeightPx;

    // About to write container/area heights: suppress the single ResizeObserver cycle our write triggers
    // so re-measuring the (now-taller) plotsRoot can't feed back and grow the container again.
    suppressSelfResizeOnce = true;

    const heightValue = `${appliedHeightPx}px`;
    containerRef.style.setProperty('min-height', heightValue, 'important');
    containerRef.style.setProperty('height', heightValue, 'important');
    if (isFullPageMode && appliedHeightPx > layoutPx + 3) {
      containerRef.style.removeProperty('max-height');
    } else {
      containerRef.style.setProperty('max-height', `${layoutPx}px`, 'important');
    }
    
    const timeseriesArea = (
      props.instanceId
        ? document.querySelector(`[data-maneuver-ts-area="${props.instanceId}"]`)
        : document.getElementById("timeseries-area")
    ) as HTMLElement | null;
    if (timeseriesArea) {
      timeseriesArea.style.setProperty("min-height", heightValue, "important");
      timeseriesArea.style.setProperty("height", "auto", "important");
      if (isFullPageMode) {
        timeseriesArea.style.setProperty("max-height", "none", "important");
        timeseriesArea.style.setProperty("overflow-y", "visible", "important");
      } else {
        timeseriesArea.style.removeProperty("max-height");
        timeseriesArea.style.removeProperty("overflow-y");
      }
    }
    
    // Verify what was actually rendered (this will be the scaled height)
    setTimeout(() => {
      const actualHeight = containerRef?.getBoundingClientRect().height;
      const computedHeight = containerRef ? getComputedStyle(containerRef).height : 'N/A';
      const timeseriesAreaHeight = timeseriesArea ? timeseriesArea.getBoundingClientRect().height : 'N/A';
      debug('Actual rendered height (scaled):', actualHeight);
      debug('Computed style height:', computedHeight);
      debug('Timeseries area height (scaled):', timeseriesAreaHeight);
    }, 100);
  };

  onMount(() => {
    const cleanupFns: (() => void)[] = [];

    setTimeout(() => {
      updateContainerHeight();
    }, 100);

    const RESIZE_DEBOUNCE_MS = 120;
    const WIDTH_EPS = 2;

    const runDebouncedResize = () => {
      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = null;
      }
      resizeDebounceTimer = setTimeout(() => {
        resizeDebounceTimer = null;
        const w =
          multiPlotsRootEl?.clientWidth ?? plotsEl?.clientWidth ?? containerRef?.clientWidth ?? 0;
        const widthChanged = Math.abs(w - lastResizeObservedWidth) > WIDTH_EPS;
        if (w > 0) {
          lastResizeObservedWidth = w;
        }
        // Skip the cycle our own height write triggered (no width change) to break the measure->grow loop.
        // Genuine width changes and external (split-drag) height changes still fall through and reflow.
        if (suppressSelfResizeOnce && !widthChanged) {
          suppressSelfResizeOnce = false;
          return;
        }
        suppressSelfResizeOnce = false;
        if (widthChanged) {
          updateChartWidth();
          rebuildLoadedCharts();
        }
        updateContainerHeight();
      }, RESIZE_DEBOUNCE_MS);
    };

    const resizeObserver = new ResizeObserver(() => {
      runDebouncedResize();
    });

    const handleWindowResize = () => {
      runDebouncedResize();
    };
    
    // Use setTimeout to ensure containerRef is available
    setTimeout(() => {
      if (containerRef) {
        resizeObserver.observe(containerRef);
      }
      // In split view the panel may not have final size yet; observe the split panel so height updates when panel is sized or resized
      const panel = containerRef?.closest('.split-panel') as HTMLElement | null;
      if (panel) {
        resizeObserver.observe(panel);
        // Delayed recalc so we run after layout and setupMediaContainerScaling; 1000ms catches late layout in split view
        const t1 = setTimeout(updateContainerHeight, 300);
        const t2 = setTimeout(updateContainerHeight, 600);
        const t3 = setTimeout(updateContainerHeight, 1000);
        cleanupFns.push(() => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); });
        cleanupFns.push(() => { if (panel) resizeObserver.unobserve(panel); });
      }
    }, 100);

    // Add window resize listener
    window.addEventListener('resize', handleWindowResize);
    
    // Add keyboard listener for GRADE updates (0-5 keys)
    const handleKeyPress = (event: KeyboardEvent) => {
      try {
        // Don't trigger if user is typing in an input field or textarea
        const target = event.target as HTMLElement | null;
        if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
          return;
        }
        
        // Only handle if there are selected events
        const selected = selectedEvents();
        if (!selected || selected.length === 0) {
          return;
        }
        
        const key = event.key;
        if (shouldBlockGradeLikeHotkeysForPerformanceSelections(key)) {
          return;
        }

        if (!['0', '1', '2', '3', '4', '5'].includes(key)) {
          return;
        }

        // Check if user is NOT a reader (readers cannot grade)
        const currentUser = user();
        if (!currentUser) {
          return;
        }
        
        // Superusers can always grade
        if (currentUser.is_super_user) {
          // Allow grading
        } else {
          // Check if user is a reader - readers cannot grade
          const userPermissions = currentUser.permissions;
          let isReader = false;
          
          if (typeof userPermissions === 'string') {
            isReader = userPermissions === 'reader';
          } else if (typeof userPermissions === 'object' && userPermissions !== null) {
            const permissionValues = Object.values(userPermissions);
            // User is a reader if ALL their permissions are 'reader'
            isReader = permissionValues.length > 0 && permissionValues.every(p => p === 'reader');
          }
          
          if (isReader) {
            return; // Readers cannot grade
          }
        }
        
        // Prevent default behavior and stop propagation
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Show confirmation dialog before proceeding
        const message = `Are you sure you want to update GRADE to ${parseInt(key, 10)} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;

        // Use window.confirm - it automatically closes when user clicks OK or Cancel
        const confirmed = window.confirm(message);
        if (!confirmed) {
          // User cancelled - dialog is already closed
          return;
        }

        // User confirmed - proceed with the update
        // Call asynchronously to avoid blocking the keydown handler
        setTimeout(() => {
          const update = performGradeUpdate(parseInt(key, 10), selected);
          update.catch(error => {
            logError('Error in maneuver hotkey update:', error);
          });
        }, 0);
      } catch (error: any) {
        logError('Error in handleKeyPress:', error);
        event.preventDefault();
        event.stopPropagation();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);

    onCleanup(() => {
      try {
        if (plotsEl) {
          d3.select(plotsEl).selectAll("*").remove();
        }
        if (multiPlotsRootEl) {
          d3.select(multiPlotsRootEl).selectAll("*").remove();
        }
        groupPlotMounts.forEach((el) => {
          if (el) d3.select(el).selectAll("*").remove();
        });
        groupPlotMounts.clear();
        groupSectionObservers.forEach((o) => o.disconnect());
        groupSectionObservers.clear();
      } catch (error) {
        logError("TimeSeries (grouped): Error cleaning up D3 selections:", error);
      }

      timeArray = [];
      valArray = [];
      eidArray = [];

      cleanupFns.forEach((fn) => fn());
      resizeObserver.disconnect();
      window.removeEventListener("resize", handleWindowResize);
      window.removeEventListener("keydown", handleKeyPress);
      if (resizeDebounceTimer) {
        clearTimeout(resizeDebounceTimer);
        resizeDebounceTimer = null;
      }
      if (selectionUpdateTimeout) {
        clearTimeout(selectionUpdateTimeout);
        selectionUpdateTimeout = null;
      }
      intersectionObservers.forEach((observer) => {
        observer.disconnect();
      });
      intersectionObservers.clear();
      storedMouseHandlers.clear();
      setTsData([]);
      setChartVisibility(new Map());
      debug("TimeSeries (grouped): Cleanup complete - all resources cleared");
    });
  });

  let lastFilteredCount = 0;
  let lastFilteredHash = '';
  let filteredUpdateTimeout: ReturnType<typeof setTimeout> | null = null;

  const getFilteredHash = (arr: unknown[]): string => {
    if (!Array.isArray(arr) || arr.length === 0) return '';
    if (typeof arr[0] === 'number') {
      const sorted = [...(arr as number[])].sort((a, b) => a - b);
      return sorted.slice(0, 10).join(',') + `_${arr.length}`;
    }
    const ids = (arr as { event_id?: number }[])
      .map((o) => o?.event_id)
      .filter((id): id is number => typeof id === 'number');
    const sorted = [...ids].sort((a, b) => a - b);
    return sorted.slice(0, 10).join(',') + `_${arr.length}`;
  };

  const extractFilteredEventIds = (): number[] => {
    const currentFiltered = filtered();
    if (!Array.isArray(currentFiltered) || currentFiltered.length === 0) return [];
    if (typeof currentFiltered[0] === "number") {
      return currentFiltered as unknown as number[];
    }
    return (currentFiltered as { event_id?: number }[])
      .map((item) => item.event_id)
      .filter((id): id is number => typeof id === "number");
  };

  createEffect(() => {
    if (!isMultiGroupMode()) return;
    const keys = groupOptionKeys();
    setGroupSeriesState((prev) => {
      const next = { ...prev };
      let changed = false;
      keys.forEach((k) => {
        if (!next[k]) {
          next[k] = { status: "idle" };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  });

  const startGroupFetchForOption = (groupKey: string) => {
    if (!isMultiGroupMode() || groupKey === "BASICS") return;
    const eventIds = extractFilteredEventIds();
    if (eventIds.length === 0) return;
    const prev = groupSeriesState()[groupKey];
    if (prev?.status === "loading" || prev?.status === "loaded") return;

    const session = multiSessionCounter;
    const signal = multiFetchAbort?.signal;
    setGroupSeriesState((p) => ({ ...p, [groupKey]: { status: "loading" } }));
    const desc = buildTimeseriesDescription(phase(), groupKey);
    fetchTimeSeriesData(context, {
      eventType: eventType(),
      description: desc,
      eventList: eventIds,
      signal,
    })
      .then((resp) => {
        if (session !== multiSessionCounter) return;
        const parsed = parseTimeSeriesResponse({
          timeseriesResponse: resp,
          eventIds,
          isEventHidden,
          tableRows: tabledata() as Array<Record<string, unknown>>,
          phaseVal: phase(),
        });
        const dataArr = parsed.dataArray as TimeSeriesDataPoint[][];
        setGroupSeriesState((p) => ({
          ...p,
          [groupKey]: {
            status: "loaded",
            data: dataArr,
            charts: parsed.charts,
            channelOrder: parsed.channelOrder,
          },
        }));
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (session !== multiSessionCounter) return;
            const mount = groupPlotMounts.get(groupKey);
            if (mount && dataArr.length > 0) {
              buildTimeSeries(dataArr, {
                mountEl: mount,
                groupKey,
                chartsOrder: parsed.charts,
                channelOrderStored: parsed.channelOrder,
              });
            }
            updateSelection();
          });
        });
      })
      .catch((err: unknown) => {
        if (session !== multiSessionCounter) return;
        logError("TimeSeries (grouped): multi group fetch failed", err);
        setGroupSeriesState((p) => ({ ...p, [groupKey]: { status: "error" } }));
      });
  };

  const flushMultiGroupUpdate = () => {
    if (firstload()) {
      setLoading(true);
    }
    multiSessionCounter += 1;
    const session = multiSessionCounter;
    if (multiFetchAbort) {
      multiFetchAbort.abort();
    }
    multiFetchAbort = new AbortController();
    const signal = multiFetchAbort.signal;

    groupSectionObservers.forEach((o) => o.disconnect());
    groupSectionObservers.clear();
    groupPlotMounts.forEach((el) => {
      if (el) d3.select(el).selectAll("*").remove();
    });
    intersectionObservers.forEach((o) => o.disconnect());
    intersectionObservers.clear();
    setChartVisibility(new Map());

    const keys = groupOptionKeys();
    const blank: Record<string, GroupSeriesEntry> = {};
    keys.forEach((k) => {
      blank[k] = { status: "idle" };
    });
    setGroupSeriesState(blank);
    setTsData([]);
    timeArray = [];
    valArray = [];
    eidArray = [];

    const eventIds = extractFilteredEventIds();
    if (eventIds.length === 0) {
      setLoading(false);
      setFirstLoad(false);
      return;
    }

    setGroupSeriesState((prev) => ({ ...prev, BASICS: { status: "loading" } }));
    const desc = buildTimeseriesDescription(phase(), "BASICS");
    fetchTimeSeriesData(context, {
      eventType: eventType(),
      description: desc,
      eventList: eventIds,
      signal,
    })
      .then((resp) => {
        if (session !== multiSessionCounter) return;
        const parsed = parseTimeSeriesResponse({
          timeseriesResponse: resp,
          eventIds,
          isEventHidden,
          tableRows: tabledata() as Array<Record<string, unknown>>,
          phaseVal: phase(),
        });
        const dataArr = parsed.dataArray as TimeSeriesDataPoint[][];
        setGroupSeriesState((prev) => ({
          ...prev,
          BASICS: {
            status: "loaded",
            data: dataArr,
            charts: parsed.charts,
            channelOrder: parsed.channelOrder,
          },
        }));
        setTsData(dataArr);
        updateMinMaxRanges();
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (session !== multiSessionCounter) return;
            const mount = groupPlotMounts.get("BASICS");
            if (mount && dataArr.length > 0) {
              buildTimeSeries(dataArr, {
                mountEl: mount,
                groupKey: "BASICS",
                chartsOrder: parsed.charts,
                channelOrderStored: parsed.channelOrder,
              });
            }
          });
        });
      })
      .catch((err: unknown) => {
        if (session !== multiSessionCounter) return;
        logError("TimeSeries (grouped): multi BASICS fetch failed", err);
        setGroupSeriesState((prev) => ({ ...prev, BASICS: { status: "error" } }));
      })
      .finally(() => {
        if (session !== multiSessionCounter) return;
        setLoading(false);
        setFirstLoad(false);
        const clearAfterLoad = () => {
          updateSelection();
        };
        if (props.instanceId) {
          queueMicrotask(clearAfterLoad);
        } else {
          setTimeout(clearAfterLoad, 500);
        }
        requestAnimationFrame(() => {
          groupOptionKeys()
            .filter((gk) => gk !== "BASICS")
            .forEach((gk) => {
              const el = containerRef?.querySelector(`[data-ts-group-section="${gk}"]`) as HTMLElement | null;
              if (!el) return;
              if (groupSectionObservers.has(gk)) return;
              const obs = new IntersectionObserver(
                (entries) => {
                  entries.forEach((entry) => {
                    if (!entry.isIntersecting) return;
                    untrack(() => startGroupFetchForOption(gk));
                  });
                },
                { rootMargin: "300px 0px 300px 0px", threshold: 0.01 }
              );
              obs.observe(el);
              groupSectionObservers.set(gk, obs);
            });
        });
      });
  };

  const flushUpdateTimeSeries = () => {
    if (isMultiGroupMode()) {
      flushMultiGroupUpdate();
      return;
    }
    if (firstload()) {
      setLoading(true);
    }
    fetchData()
      .then((data) => {
        buildTimeSeries(data);
        setFirstLoad(false);
      })
      .catch((err: unknown) => {
        logError("TimeSeries (grouped): Failed to refresh time series data", err);
      })
      .finally(() => {
        const clearAfterLoad = () => {
          setLoading(false);
          if (tsdata().length > 0) {
            updateSelection();
          }
        };
        if (props.instanceId) {
          queueMicrotask(clearAfterLoad);
        } else {
          setTimeout(clearAfterLoad, 500);
        }
      });
  };

  const updateTimeSeries = () => {
    if (updateTimeSeriesDebounce) {
      clearTimeout(updateTimeSeriesDebounce);
    }
    const ms = firstload() ? 0 : 48;
    updateTimeSeriesDebounce = setTimeout(() => {
      updateTimeSeriesDebounce = null;
      flushUpdateTimeSeries();
    }, ms);
  };

  createEffect(() => {
    if (!isPanelActive()) return;
    if (triggerUpdate()) {
      setTriggerUpdate(false);
      if (filteredUpdateTimeout) {
        clearTimeout(filteredUpdateTimeout);
        filteredUpdateTimeout = null;
      }
      const arr = filtered();
      const n = Array.isArray(arr) ? arr.length : 0;
      if (n > 0) {
        lastFilteredHash = getFilteredHash(arr);
        lastFilteredCount = n;
      }
      queueMicrotask(() => {
        updateTimeSeries();
      });
    }
  });

  createEffect(() => {
    if (!isPanelActive()) return;
    const currentFiltered = filtered();
    const currentFilteredCount = currentFiltered?.length || 0;
    const hasTriggerUpdate = untrack(() => triggerUpdate());

    const filteredHash = getFilteredHash(Array.isArray(currentFiltered) ? currentFiltered : []);

    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }

    if (currentFilteredCount === 0) {
      lastFilteredCount = 0;
      lastFilteredHash = '';
      return;
    }

    const justGotData = lastFilteredCount === 0 && currentFilteredCount > 0;
    const dataChanged = filteredHash !== lastFilteredHash || justGotData;

    if (currentFilteredCount > 0 && dataChanged && !hasTriggerUpdate) {
      const delay = justGotData ? 50 : 100;
      filteredUpdateTimeout = setTimeout(() => {
        const stillHasTrigger = untrack(() => triggerUpdate());
        const currentHash = getFilteredHash(Array.isArray(filtered()) ? filtered() : []);
        const shouldUpdate =
          !stillHasTrigger &&
          (currentHash !== lastFilteredHash || justGotData) &&
          currentHash.length > 0;
        if (shouldUpdate) {
          updateTimeSeries();
          lastFilteredHash = currentHash;
        }
        filteredUpdateTimeout = null;
      }, delay);
    }

    lastFilteredCount = currentFilteredCount;
    if (filteredHash && !filteredUpdateTimeout) {
      lastFilteredHash = filteredHash;
    }
  });

  onCleanup(() => {
    if (filteredUpdateTimeout) {
      clearTimeout(filteredUpdateTimeout);
      filteredUpdateTimeout = null;
    }
    if (updateTimeSeriesDebounce) {
      clearTimeout(updateTimeSeriesDebounce);
      updateTimeSeriesDebounce = null;
    }
  });

  createEffect(on([() => props?.description, phase], () => {
    if (isMultiGroupMode()) return;
    if (untrack(() => !firstload() && tsdata().length > 0)) {
      updateTimeSeries();
    }
  }));

  createEffect(
    on(phase, () => {
      if (!isMultiGroupMode()) return;
      if (untrack(() => firstload())) return;
      updateTimeSeries();
    })
  );

  createEffect(() => {
    if (!isMultiGroupMode()) return;
    const cn = persistantStore.selectedClassName();
    const pid = persistantStore.selectedProjectId();
    if (!cn || !pid) {
      setGroupOptionKeys(["BASICS"]);
      return;
    }
    let active = true;
    void (async () => {
      const opts = await fetchManeuverTimeseriesOptions(String(cn), String(pid));
      if (!active) return;
      setGroupOptionKeys(opts.length ? opts : ["BASICS"]);
    })();
    onCleanup(() => {
      active = false;
    });
  });

  createEffect(() => {
    if (triggerSelection()) {
      updateSelection()
    }
  });

  // Watch for selectedGroupKeys changes (from cross-window sync or table/map) and update selection
  let selectionUpdateTimeout: NodeJS.Timeout | null = null;
  createEffect(() => {
    selectedGroupKeys();
    
    // Update selection when selectedGroupKeys changes
    // Only update if component is ready (data loaded)
    if (tsdata().length > 0) {
      // Clear any pending update
      if (selectionUpdateTimeout) {
        clearTimeout(selectionUpdateTimeout);
      }
      
      // Debounce updates to batch rapid changes (e.g., when clicking multiple legend items)
      selectionUpdateTimeout = setTimeout(() => {
        updateSelection();
        selectionUpdateTimeout = null;
      }, 50); // 50ms debounce - fast enough to feel responsive, slow enough to batch updates
    }
  });

  // Watch for color changes - update selection when color changes
  // This ensures the maneuver window updates when color changes in the main window
  createEffect(() => {
    // Update selection when color changes and component is ready (data loaded)
    if (tsdata().length > 0) {
      // Use untrack to prevent updateSelection from creating reactive dependencies
      untrack(() => {
        updateSelection();
      });
    }
  });

  // Watch for groupDisplayMode changes (OFF/ON/MIX) — redraw so layers and hover match mode
  // Also trigger on initial run when mode is MIX so first build uses MIX (fix 4)
  let prevGroupDisplayMode: string | undefined;
  createEffect(() => {
    const mode = groupDisplayMode();
    const isModeChange = prevGroupDisplayMode !== undefined && prevGroupDisplayMode !== mode;
    const isInitialMIX = prevGroupDisplayMode === undefined && mode === 'MIX';
    if (isModeChange || isInitialMIX) {
      untrack(() => {
        if (tsdata().length > 0) setTriggerUpdate(true);
      });
    }
    prevGroupDisplayMode = mode;
  });


  return (
    <>
      {firstload() && <Loading class="maneuver-ts-loading-overlay" />}
      <div
        class="time-series"
        ref={(el) => {
          containerRef = el;
        }}
        style={{
          opacity: loading() ? 0.2 : 1,
          "pointer-events": loading() ? "none" : "auto",
        }}
      >
        <Show
          when={isMultiGroupMode()}
          fallback={
            <div
              class="maneuver-time-series maneuver-ts-plots-root"
              ref={(el) => {
                plotsEl = el;
              }}
            />
          }
        >
          <div
            class="maneuver-time-series maneuver-ts-multi-root"
            ref={(el) => {
              multiPlotsRootEl = el;
              plotsEl = null;
            }}
          >
            <For each={groupOptionKeys()}>
              {(gk) => (
                <div class="group-container" data-ts-group-section={gk}>
                  <div class="break">
                    <h2>{gk}</h2>
                  </div>
                  <Show when={gk !== "BASICS" && groupSeriesState()[gk]?.status !== "loaded"}>
                    <div class="maneuver-ts-group-placeholder" aria-hidden="true" />
                  </Show>
                  <div
                    class="maneuver-ts-group-plots maneuver-time-series maneuver-ts-plots-root"
                    data-group={gk}
                    ref={(el) => {
                      if (el) {
                        groupPlotMounts.set(gk, el);
                      } else {
                        groupPlotMounts.delete(gk);
                      }
                    }}
                  />
                </div>
              )}
            </For>
          </div>
        </Show>
      </div>
      <Portal mount={typeof document !== "undefined" ? document.body : undefined}>
        <div id="maneuver-timeseries-tooltip" class="tooltip" style={{
            opacity: tooltip().visible ? 1 : 0,
            left: `${tooltip().x}px`,
            top: `${tooltip().y}px`,
            position: "fixed",
            "pointer-events": "none",
            "z-index": 9999
          }} innerHTML={sanitizeTooltipHtml(tooltip().content)} />
      </Portal>
    </>
  );
}
